import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import styles from './TaskManagement.module.css';
import { addTask, updateTask, deleteTask, getTasks } from '../../Fetch/fetchTasks';
import { getAllSuppliers, commonServiceTypes } from '../../Fetch/fetchSupplier';
import { uploadImage, deleteImage, getImage, validateFile, formatFileSize } from '../../Fetch/fetchUploadImage';
import { Edit, Trash, ExternalLink, Mail, Upload, Camera, Eye } from 'lucide-react';

// External link utilities - Create this file if it doesn't exist
const generateExternalTaskLink = async (buildingId, taskId) => {
  try {
    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';
    
    const response = await fetch(`${API_URL}/api/ExternalTask/GenerateLink`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        buildingId: buildingId,
        taskId: taskId
      }),
    });

    if (!response.ok) {
      throw new Error('שגיאה ביצירת הקישור החיצוני');
    }

    const data = await response.json();

    if (data.success) {
      return data.externalUrl;
    } else {
      throw new Error(data.message || 'שגיאה ביצירת הקישור החיצוני');
    }
  } catch (error) {
    throw error;
  }
};

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      return success;
    }
  } catch (error) {
    return false;
  }
};

const shareTaskViaEmail = (task, building, externalUrl) => {
  const taskDescription = task.Description || task.description || 'משימה';
  const taskStatus = task.Status || task.status || 'לא ידוע';
  const taskPriority = task.Priority || task.priority || 'לא ידוע';
  const taskDate = task.Date || task.date || 'לא נקבע';
  const taskUser = task.TaskUser || task.taskUser || 'לא נקבע';
  const taskId = task.TasksID || task.tasksID || 'לא ידוע';

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('he-IL');
    } catch {
      return dateString;
    }
  };

  const subject = `משימה #${taskId} - ${building?.address || building?.Address || 'ועד הבית'}`;
  
  const body = `שלום,

אנא עיין במשימה הבאה מועד הבית:

פרטי המשימה:
• בניין: ${building?.address || building?.Address || 'לא ידוע'}
• מספר משימה: #${taskId}
• תיאור: ${taskDescription}
• עדיפות: ${taskPriority}
• סטטוס נוכחי: ${taskStatus}
• תאריך יעד: ${formatDate(taskDate)}
• אחראי נוכחי: ${taskUser}

לעדכון המשימה, אנא לחץ על הקישור הבא:
${externalUrl}

באמצעות הקישור ניתן לעדכן את סטטוס המשימה ולהוסיף הערות ללא צורך בהרשמה למערכת.

תודה רבה,
ועד הבית`;

  const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = emailUrl;
};

export default function BuildingCommitteeTaskManagement({ buildingId = 1 }) {
  const location = useLocation();
  const isInitialLoad = useRef(true);
  const loadingRef = useRef(false);

  const [tasks, setTasks] = useState([]);
  const [building, setBuilding] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeBuildingId, setActiveBuildingId] = useState(buildingId);
  const [newTask, setNewTask] = useState({
    Description: "",
    Status: "פתוח",
    Priority: "בינוני",
    Date: "",
    TaskUser: "",
    TaskImageURL: "",
    BuildingID: buildingId
  });
  const [suggestedVendors, setSuggestedVendors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [isMobile, setIsMobile] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showAllVendors, setShowAllVendors] = useState(false);
  const [isVendorsCollapsed, setIsVendorsCollapsed] = useState(false);
  
  // Image upload states
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTaskImage, setSelectedTaskImage] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // CONSOLIDATED INITIALIZATION
  useEffect(() => {

    const initializeComponent = async () => {
      try {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);

        const userData = JSON.parse(localStorage.getItem('user'));
        const currentBuildingId = userData?.buildingId || buildingId;
        setBuilding(userData.currentBuilding);
        setActiveBuildingId(currentBuildingId);
        setNewTask(prev => ({ ...prev, BuildingID: currentBuildingId }));

        loadSuppliers();

        setTimeout(() => {
          loadTasks(currentBuildingId);
        }, 100);

        const handleBuildingChange = () => {
          const newUserData = JSON.parse(localStorage.getItem('user'));
          const newBuildingId = newUserData?.buildingId;

          if (newBuildingId && newBuildingId !== currentBuildingId) {
            setActiveBuildingId(newBuildingId);
            setNewTask(prev => ({ ...prev, BuildingID: newBuildingId }));
            loadTasks(newBuildingId);
          }
        };

        window.addEventListener('buildingChanged', handleBuildingChange);
        window.addEventListener('userDataChanged', handleBuildingChange);

        return () => {
          window.removeEventListener('resize', checkMobile);
          window.removeEventListener('buildingChanged', handleBuildingChange);
          window.removeEventListener('userDataChanged', handleBuildingChange);
        };

      } catch (error) {
        setError('שגיאה באתחול הרכיב');
      }
    };

    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      initializeComponent();
    }
  }, []);

  // NAVIGATION HANDLING
  useEffect(() => {
    if (!location.state?.fromNotifications || !location.state?.editingTask) {
      return;
    }

    const navigationTask = location.state.editingTask;

    const handleNavigationTask = () => {
      if (tasks.length === 0) {
        return;
      }


      const taskWithPascalCase = {
        TasksID: navigationTask.TasksID || navigationTask.tasksID,
        Description: navigationTask.Description || navigationTask.description || "",
        Status: navigationTask.Status || navigationTask.status || "",
        Priority: navigationTask.Priority || navigationTask.priority || "",
        Date: navigationTask.Date || navigationTask.date ?
          new Date(navigationTask.Date || navigationTask.date).toISOString().split('T')[0] : "",
        TaskUser: navigationTask.TaskUser || navigationTask.taskUser || "",
        TaskImageURL: navigationTask.TaskImageURL || navigationTask.taskImageURL || "",
        BuildingID: navigationTask.BuildingID || navigationTask.buildingID || activeBuildingId
      };

      setEditingTask(taskWithPascalCase);
      setShowNewTaskForm(false);

      const relevant = findRelevantVendors(taskWithPascalCase.Description);
      setSuggestedVendors(relevant);

      window.history.replaceState({}, document.title);
    };

    if (tasks.length > 0) {
      handleNavigationTask();
    } else {
      const timeoutId = setTimeout(handleNavigationTask, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [tasks.length, location.state, activeBuildingId]);

  // FILTERING EFFECT
  useEffect(() => {

    let results = [...tasks];

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      results = results.filter(task => {
        const description = (task.Description || task.description || "").toString().toLowerCase();
        const status = (task.Status || task.status || "").toString().toLowerCase();
        const taskUser = (task.TaskUser || task.taskUser || "").toString().toLowerCase();
        const priority = (task.Priority || task.priority || "").toString().toLowerCase();
        const taskId = (task.TasksID || task.tasksID || "").toString().toLowerCase();

        return description.includes(searchLower) ||
          status.includes(searchLower) ||
          taskUser.includes(searchLower) ||
          priority.includes(searchLower) ||
          taskId.includes(searchLower);
      });
    }

    if (statusFilter) {
      results = results.filter(task =>
        (task.Status || task.status) === statusFilter
      );
    }

    if (priorityFilter) {
      results = results.filter(task =>
        (task.Priority || task.priority) === priorityFilter
      );
    }

    setFilteredTasks(results);
    setCurrentPage(1);
  }, [tasks, searchTerm, statusFilter, priorityFilter]);

  // MODAL KEYBOARD HANDLING
  useEffect(() => {
    const handleIOSKeyboard = () => {
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        const modalBody = document.querySelector(`.${styles.modalBody}`);
        const inputs = document.querySelectorAll(`.${styles.formGroup} input, .${styles.formGroup} select`);

        inputs.forEach(input => {
          input.addEventListener('focus', () => {
            setTimeout(() => {
              if (modalBody) {
                input.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'nearest'
                });
              }
            }, 300);
          });

          input.addEventListener('blur', () => {
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 300);
          });
        });
      }
    };

    if (showNewTaskForm || editingTask) {
      handleIOSKeyboard();
    }

    return () => {
      const inputs = document.querySelectorAll(`.${styles.formGroup} input, .${styles.formGroup} select`);
      inputs.forEach(input => {
        input.removeEventListener('focus', handleIOSKeyboard);
        input.removeEventListener('blur', handleIOSKeyboard);
      });
    };
  }, [showNewTaskForm, editingTask, styles.modalBody, styles.formGroup]);

  // ESC KEY HANDLING
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (showNewTaskForm || editingTask) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showNewTaskForm, editingTask]);

  // Load suppliers from database
  const loadSuppliers = async () => {
    try {
      const response = await getAllSuppliers();
      setSuppliers(response || []);
    } catch (error) {
      setSuppliers([]);
    }
  };

  // LOAD TASKS FUNCTION
  const loadTasks = async (buildingIdToUse = activeBuildingId) => {
    if (!buildingIdToUse) {
      return;
    }

    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError("");


      const response = await getTasks(buildingIdToUse);

      if (response.success) {
        const tasksData = response.tasks || [];
        
        // Prevent duplicate tasks by filtering unique TasksID
        const uniqueTasks = tasksData.filter((task, index, self) => 
          index === self.findIndex(t => (t.TasksID || t.tasksID) === (task.TasksID || task.tasksID))
        );
        
        if (uniqueTasks.length !== tasksData.length) {
        }
        
        setTasks(uniqueTasks);
        setError("");
      } else {
        const errorMsg = "שגיאה בטעינת המשימות: " + (response.message || "שגיאה לא ידועה");
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = "שגיאה בטעינת המשימות: " + err.message;
      setError(errorMsg);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  // Find relevant vendors based on task description
  const findRelevantVendors = (description) => {
    if (!description || !suppliers.length) return [];

    const descriptionLower = description.toLowerCase().trim();
    const descriptionWords = descriptionLower.split(/\s+/);

    const scoredSuppliers = suppliers.map(supplier => {
      const serviceType = supplier.serviceType || '';
      const serviceTypes = serviceType.split(';').map(type => type.trim().toLowerCase()).filter(type => type);

      let score = 0;
      let matchedServices = [];

      serviceTypes.forEach(service => {
        if (service && descriptionWords.some(word => word.includes(service) || service.includes(word))) {
          score += 10;
          matchedServices.push(service);
        }
      });

      if (matchedServices.length === 0) {
        serviceTypes.forEach(service => {
          if (service && descriptionLower.includes(service)) {
            score += 5;
            matchedServices.push(service);
          }
        });
      }

      if (matchedServices.length > 1) {
        score += matchedServices.length * 2;
      }

      const supplierNameLower = (supplier.name || '').toLowerCase();
      if (descriptionWords.some(word => supplierNameLower.includes(word))) {
        score += 2;
      }

      return {
        ...supplier,
        relevanceScore: score,
        matchedServices: matchedServices,
        isHighPriority: score >= 10,
        supplierPriority: supplier.priority || 10
      };
    })
      .filter(supplier => supplier.relevanceScore > 0)
      .sort((a, b) => {
        if (b.relevanceScore !== a.relevanceScore) {
          return b.relevanceScore - a.relevanceScore;
        }
        if (a.supplierPriority !== b.supplierPriority) {
          return a.supplierPriority - b.supplierPriority;
        }
        if (b.matchedServices.length !== a.matchedServices.length) {
          return b.matchedServices.length - a.matchedServices.length;
        }
        return (a.name || '').localeCompare(b.name || '');
      });

    return scoredSuppliers;
  };

  useEffect(() => {
  }, [building])

  // WHATSAPP SHARE WITH EXTERNAL LINK
  const handleWhatsAppShare = async (task) => {
    try {
      setLoading(true);
      
      const taskDescription = task.Description || task.description || 'משימה';
      const taskStatus = task.Status || task.status || 'לא ידוע';
      const taskPriority = task.Priority || task.priority || 'לא ידוע';
      const taskDate = task.Date || task.date || 'לא נקבע';
      const taskUser = task.TaskUser || task.taskUser || 'לא נקבע';
      const taskId = task.TasksID || task.tasksID || 'לא ידוע';
      const isCompleted = taskStatus === 'הושלם';

      // Generate external link only if task is not completed
      let externalUrl = '';
      if (!isCompleted) {
        try {
          const buildingId = task.BuildingID || task.buildingID || activeBuildingId;
          
          externalUrl = await generateExternalTaskLink(buildingId, taskId);
        } catch (error) {
          alert('שגיאה ביצירת קישור חיצוני: ' + error.message + '\nהודעת WhatsApp תישלח ללא קישור.');
        }
      } else {
      }

      const message = `📋 דוח משימה מועד הבית

🏢 בניין: ${building?.address || building?.Address || 'לא ידוע'}
🆔 מספר משימה: #${taskId}

📝 תיאור: ${taskDescription}
⚡ עדיפות: ${taskPriority}
📊 סטטוס: ${taskStatus}
📅 תאריך יעד: ${formatDate(taskDate)}
👤 אחראי: ${taskUser}

${suggestedVendors.length > 0 ? 
`🔧 ספקים מומלצים:
${suggestedVendors.slice(0, 3).map(vendor => 
  `• ${vendor.name}${vendor.phone ? ` - ${vendor.phone}` : ''}`
).join('\n')}

` : ''}${externalUrl ? `🔗 קישור לעדכון המשימה:
${externalUrl}

💡 באמצעות הקישור ניתן לעדכן את סטטוס המשימה ולהוסיף הערות.

` : ''}${isCompleted ? '✅ המשימה הושלמה - אין צורך בעדכון נוסף.' : 'נא לטפל בהקדם האפשרי.'}

תודה רבה!`;


      const encodedMessage = encodeURIComponent(message);
      
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                            (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform)) ||
                            window.innerWidth <= 768;
      
      if (isMobileDevice) {
        const nativeUrl = `whatsapp://send?text=${encodedMessage}`;
        const webUrl = `https://web.whatsapp.com/send?text=${encodedMessage}`;
        
        window.location.href = nativeUrl;
        
        setTimeout(() => {
          if (!document.hidden && document.visibilityState === 'visible') {
            window.open(webUrl, '_blank', 'noopener,noreferrer');
          }
        }, 2500);
      } else {
        const whatsappUrl = `https://web.whatsapp.com/send?text=${encodedMessage}`;
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      alert('שגיאה בשיתוף ב-WhatsApp: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate and copy external task link
  const handleGenerateExternalLink = async (task) => {
    try {
      setLoading(true);
      const buildingId = task.BuildingID || task.buildingID || activeBuildingId;
      const taskId = task.TasksID || task.tasksID;

      const externalUrl = await generateExternalTaskLink(buildingId, taskId);
      
      const success = await copyToClipboard(externalUrl);
      
      if (success) {
        alert('קישור חיצוני הועתק ללוח! כעת ניתן לשלוח אותו לכל מי שצריך לעדכן את המשימה.');
      } else {
        prompt('קישור חיצוני (העתק ידנית):', externalUrl);
      }
    } catch (error) {
      alert('שגיאה ביצירת הקישור החיצוני: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Share task via email with external link
  const handleEmailShare = async (task) => {
    try {
      setLoading(true);
      const buildingId = task.BuildingID || task.buildingID || activeBuildingId;
      const taskId = task.TasksID || task.tasksID;

      const externalUrl = await generateExternalTaskLink(buildingId, taskId);
      shareTaskViaEmail(task, building, externalUrl);
    } catch (error) {
      alert('שגיאה בשיתוף באימייל: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    const taskData = editingTask ? editingTask : newTask;
    const setTaskData = editingTask ? setEditingTask : setNewTask;

    setTaskData({ ...taskData, [name]: value });

    if (name === "Description") {
      const relevant = findRelevantVendors(value);
      setSuggestedVendors(relevant);
    }
  };

  // Clear filters function
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPriorityFilter('');
  };

  // Close modal function
  const closeModal = () => {
    setShowNewTaskForm(false);
    setEditingTask(null);
    setSuggestedVendors([]);
    setShowAllVendors(false);
    setIsVendorsCollapsed(false);
    setNewTask({
      Description: "",
      Status: "פתוח",
      Priority: "בינוני",
      Date: "",
      TaskUser: "",
      TaskImageURL: "",
      BuildingID: activeBuildingId,
      uploadedFile: null
    });
  };

  // Handle modal backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Validate task data
  const validateTaskData = (taskData) => {
    if (!taskData.Description?.trim()) {
      return "תיאור המשימה הוא שדה חובה";
    }
    if (!taskData.Date) {
      return "תאריך יעד הוא שדה חובה";
    }
    if (!taskData.TaskUser?.trim()) {
      return "שם האחראי הוא שדה חובה";
    }
    return null;
  };

  // Add new task
  const handleAddTask = async () => {
    const validationError = validateTaskData(newTask);
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const taskToAdd = {
        ...newTask,
        BuildingID: activeBuildingId,
        Date: newTask.Date ? new Date(newTask.Date).toISOString() : null
      };

      const response = await addTask(taskToAdd);
      if (response.success) {
        // Add the new task to the existing list instead of reloading everything
        const newTaskData = response.task || { ...taskToAdd, TasksID: response.taskId || Date.now() };
        setTasks(prevTasks => {
          // Check if task already exists to prevent duplicates
          const existingTaskIndex = prevTasks.findIndex(t => 
            (t.TasksID || t.tasksID) === (newTaskData.TasksID || newTaskData.tasksID)
          );
          
          if (existingTaskIndex >= 0) {
            return prevTasks;
          }
          
          return [newTaskData, ...prevTasks];
        });
        
        closeModal();
        alert("המשימה נוספה בהצלחה");
      } else {
        setError("שגיאה בהוספת המשימה: " + (response.message || "שגיאה לא ידועה"));
      }
    } catch (err) {
      setError("שגיאה בהוספת המשימה: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update existing task
  const handleUpdateTask = async () => {
    const validationError = validateTaskData(editingTask);
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const taskToUpdate = {
        ...editingTask,
        Date: editingTask.Date ? new Date(editingTask.Date).toISOString() : null
      };

      const response = await updateTask(taskToUpdate);
      if (response.success) {
        await loadTasks(activeBuildingId);
        closeModal();
        alert("המשימה עודכנה בהצלחה");
      } else {
        setError("שגיאה בעדכון המשימה: " + (response.message || "שגיאה לא ידועה"));
      }
    } catch (err) {
      setError("שגיאה בעדכון המשימה: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete task
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק את המשימה?")) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Find the task to get its image path before deletion
      const taskToDelete = tasks.find(task => 
        (task.TasksID || task.tasksID) === taskId
      );

      // Delete associated image file if exists
      if (taskToDelete && (taskToDelete.TaskImageURL || taskToDelete.taskImageURL)) {
        const imagePath = taskToDelete.TaskImageURL || taskToDelete.taskImageURL;
        try {
          await deleteImage(imagePath, activeBuildingId);
          console.log('Associated image file deleted:', imagePath);
        } catch (imageError) {
          console.warn('Failed to delete associated image file:', imageError);
          // Don't stop the task deletion if image deletion fails
        }
      }

      // Delete the task from database
      const response = await deleteTask({ TasksID: taskId });
      if (response.success) {
        await loadTasks(activeBuildingId);
        alert("המשימה והקבצים הקשורים נמחקו בהצלחה");
      } else {
        setError("שגיאה במחיקת המשימה: " + (response.message || "שגיאה לא ידועה"));
      }
    } catch (err) {
      setError("שגיאה במחיקת המשימה: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Start editing task
  const startEditTask = (task) => {
    const taskWithPascalCase = {
      TasksID: task.TasksID || task.tasksID,
      Description: task.Description || task.description || "",
      Status: task.Status || task.status || "",
      Priority: task.Priority || task.priority || "",
      Date: task.Date || task.date ? new Date(task.Date || task.date).toISOString().split('T')[0] : "",
      TaskUser: task.TaskUser || task.taskUser || "",
      TaskImageURL: task.TaskImageURL || task.taskImageURL || "",
      BuildingID: task.BuildingID || task.buildingID || activeBuildingId
    };

    setEditingTask(taskWithPascalCase);
    setShowNewTaskForm(false);
    const relevant = findRelevantVendors(taskWithPascalCase.Description);
    setSuggestedVendors(relevant);
  };

  // Sort tasks
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });

    setFilteredTasks(prevTasks => {
      return [...prevTasks].sort((a, b) => {
        const aValue = a[key] || a[key.toLowerCase()] || "";
        const bValue = b[key] || b[key.toLowerCase()] || "";

        if (aValue < bValue) {
          return direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    });
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTasks.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // File upload handlers
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const userData = JSON.parse(localStorage.getItem('user'));
    const currentBuildingId = activeBuildingId ?? userData?.buildingId;

    if (currentBuildingId === null || currentBuildingId === undefined) {
      alert('שגיאה: לא נמצא מזהה בניין. אנא התחבר מחדש.');
      return;
    }

    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadImage(file, currentBuildingId, 'משימה - העלאת קובץ', 'tasks');

      if (result.success) {
        // Update the current task (either new task or editing task)
        const uploadedFile = {
          name: file.name,
          size: file.size,
          path: result.data.filePath,
          url: result.data.relativePath
        };

        if (editingTask) {
          setEditingTask(prev => ({
            ...prev,
            TaskImageURL: result.data.filePath,
            uploadedFile: uploadedFile
          }));
        } else {
          setNewTask(prev => ({
            ...prev,
            TaskImageURL: result.data.filePath,
            uploadedFile: uploadedFile
          }));
        }

        alert('הקובץ הועלה בהצלחה!');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('שגיאה בהעלאת הקובץ: ' + error.message);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleCameraCapture = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const userData = JSON.parse(localStorage.getItem('user'));
    const currentBuildingId = activeBuildingId ?? userData?.buildingId;

    if (currentBuildingId === null || currentBuildingId === undefined) {
      alert('שגיאה: לא נמצא מזהה בניין. אנא התחבר מחדש.');
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadImage(file, currentBuildingId, 'משימה - צילום תמונה', 'tasks');

      if (result.success) {
        // Update the current task (either new task or editing task)
        const uploadedFile = {
          name: file.name,
          size: file.size,
          path: result.data.filePath,
          url: result.data.relativePath
        };

        if (editingTask) {
          setEditingTask(prev => ({
            ...prev,
            TaskImageURL: result.data.filePath,
            uploadedFile: uploadedFile
          }));
        } else {
          setNewTask(prev => ({
            ...prev,
            TaskImageURL: result.data.filePath,
            uploadedFile: uploadedFile
          }));
        }

        alert('התמונה נשמרה בהצלחה!');
      }
    } catch (error) {
      console.error('Error uploading camera image:', error);
      alert('שגיאה בשמירת התמונה: ' + error.message);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const removeUploadedFile = async () => {
    const currentTask = editingTask || newTask;
    if (!currentTask.uploadedFile) return;

    try {
      // Delete from server
      await deleteImage(currentTask.uploadedFile.path, activeBuildingId);

      // Update the current task
      if (editingTask) {
        setEditingTask(prev => ({
          ...prev,
          TaskImageURL: "",
          uploadedFile: null
        }));
      } else {
        setNewTask(prev => ({
          ...prev,
          TaskImageURL: "",
          uploadedFile: null
        }));
      }

      alert('הקובץ הוסר בהצלחה!');
    } catch (error) {
      console.error('Error removing file:', error);
      alert('שגיאה בהסרת הקובץ: ' + error.message);
    }
  };

  const viewTaskImage = async () => {
    const currentTask = editingTask || newTask;
    const imagePath = currentTask.uploadedFile?.path || currentTask.TaskImageURL;
    
    if (!imagePath) {
      alert('אין תמונה להצגה');
      return;
    }

    try {
      // Use the same approach as expenses - fetch image and create blob URL
      const response = await getImage(imagePath);

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setSelectedTaskImage(blobUrl);
      } else {
        alert('שגיאה בטעינת התמונה');
      }
    } catch (error) {
      console.error('Error viewing image:', error);
      alert('שגיאה בהצגת התמונה: ' + error.message);
    }
  };

  const removeExistingImage = async () => {
    if (!editingTask || !editingTask.TaskImageURL) {
      alert('אין תמונה קיימת להסרה');
      return;
    }

    const confirmDelete = window.confirm('האם אתה בטוח שברצונך למחוק את התמונה הקיימת?');
    if (!confirmDelete) return;

    try {
      // Delete from server using the same deleteImage function
      await deleteImage(editingTask.TaskImageURL, activeBuildingId);

      // Update the editing task to remove the image URL
      setEditingTask(prev => ({
        ...prev,
        TaskImageURL: ""
      }));

      // Also update the task in the tasks list for immediate UI feedback
      setTasks(prevTasks => 
        prevTasks.map(task => 
          (task.TasksID || task.tasksID) === (editingTask.TasksID || editingTask.tasksID)
            ? { ...task, TaskImageURL: "", taskImageURL: "" }
            : task
        )
      );

      alert('התמונה הקיימת הוסרה בהצלחה!');
    } catch (error) {
      console.error('Error removing existing image:', error);
      alert('שגיאה בהסרת התמונה הקיימת: ' + error.message);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString('he-IL');
    } catch {
      return dateString;
    }
  };

  // Get priority color class
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'גבוה': return styles.priorityHigh;
      case 'בינוני': return styles.priorityMedium;
      case 'נמוך': return styles.priorityLow;
      default: return '';
    }
  };

  // Get status color class
  const getStatusClass = (status) => {
    switch (status) {
      case 'פתוח': return styles.statusOpen;
      case 'בטיפול': return styles.statusInProgress;
      case 'הושלם': return styles.statusCompleted;
      default: return '';
    }
  };

  // Action Menu Modal Component
  const ActionMenu = ({ task, onEdit, onDelete, onWhatsAppShare, onGenerateLink, onEmailShare, loading }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isCompleted = (task.Status || task.status) === 'הושלם';

    const actions = [
      {
        label: '📱 שתף WhatsApp',
        onClick: () => onWhatsAppShare(task),
        disabled: loading,
        title: 'שתף ב-WhatsApp' + (isCompleted ? ' (כולל הודעה שהמשימה הושלמה)' : ' עם קישור לעדכון')
      },
      {
        label: '🔗 קישור חיצוני',
        onClick: () => onGenerateLink(task),
        disabled: loading || isCompleted,
        title: isCompleted ? 'המשימה הושלמה - אין צורך בקישור עדכון' : 'צור קישור חיצוני לעדכון המשימה'
      },
      {
        label: '📧 שתף באימייל',
        onClick: () => onEmailShare(task),
        disabled: loading || isCompleted,
        title: isCompleted ? 'המשימה הושלמה - אין צורך בשיתוף' : 'שתף באימייל עם קישור לעדכון'
      },
      {
        label: '✏️ ערוך',
        onClick: () => onEdit(task),
        disabled: loading
      },
      {
        label: '🗑️ מחק',
        onClick: () => onDelete(task.TasksID || task.tasksID),
        disabled: loading,
        isDanger: true
      }
    ];

    const handleActionClick = (action) => {
      if (!action.disabled) {
        action.onClick();
        setIsOpen(false);
      }
    };

    return (
      <>
        <button
          className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
          onClick={() => setIsOpen(true)}
          disabled={loading}
          aria-label="פתח תפריט פעולות"
        >
          <span>⋯</span>
        </button>

        {isOpen && createPortal(
          <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
            <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.actionMenuHeader}>
                <h3>פעולות</h3>
                <button
                  className={styles.closeButton}
                  onClick={() => setIsOpen(false)}
                  aria-label="סגור"
                >
                  ✕
                </button>
              </div>
              <div className={styles.actionMenuContent}>
                {actions.map((action, index) => (
                  <button
                    key={index}
                    className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                    onClick={() => handleActionClick(action)}
                    disabled={action.disabled}
                    title={action.title}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Statistics
  const totalTasks = tasks.length;
  const openTasks = tasks.filter(task => (task.Status || task.status) === 'פתוח').length;
  const inProgressTasks = tasks.filter(task => (task.Status || task.status) === 'בטיפול').length;
  const completedTasks = tasks.filter(task => (task.Status || task.status) === 'הושלם').length;

  // Render mobile cards
  const renderMobileCards = () => {
    if (currentItems.length === 0) {
      return (
        <div className={styles.emptyState}>
          {searchTerm || statusFilter || priorityFilter ? 'לא נמצאו משימות התואמות לחיפוש' : 'אין משימות'}
        </div>
      );
    }

    return (
      <div className={styles.mobileCardsContainer}>
        <div className={styles.cardsGrid}>
          {currentItems.map((task, index) => (
            <div key={task.TasksID || task.tasksID || `task-${index}`} className={styles.dataCard}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                  <span className={styles.cardIcon}>#</span>
                  <span>משימה {task.TasksID || task.tasksID}</span>
                </div>
                <div className={styles.cardHeaderActions}>
                  <span className={`${styles.statusBadge} ${getStatusClass(task.Status || task.status)}`}>
                    {task.Status || task.status}
                  </span>
                  <ActionMenu
                    task={task}
                    onEdit={startEditTask}
                    onDelete={handleDeleteTask}
                    onWhatsAppShare={handleWhatsAppShare}
                    onGenerateLink={handleGenerateExternalLink}
                    onEmailShare={handleEmailShare}
                    loading={loading}
                  />
                </div>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.cardInfo}>
                  <span className={styles.infoLabel}>תיאור:</span>
                  <span className={styles.infoValue}>{task.Description || task.description}</span>
                </div>

                <div className={styles.cardInfo}>
                  <span className={styles.infoLabel}>עדיפות:</span>
                  <span className={`${styles.priorityBadge} ${getPriorityClass(task.Priority || task.priority)}`}>
                    {task.Priority || task.priority}
                  </span>
                </div>

                <div className={styles.cardInfo}>
                  <span className={styles.infoLabel}>תאריך יעד:</span>
                  <span className={styles.infoValue}>{formatDate(task.Date || task.date)}</span>
                </div>

                <div className={styles.cardInfo}>
                  <span className={styles.infoLabel}>אחראי:</span>
                  <span className={styles.infoValue}>{task.TaskUser || task.taskUser}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render desktop table
  const renderDesktopTable = () => {
    return (
      <div className={styles.tableContainer}>
        <table className={styles.tasksTable}>
          <thead>
            <tr>
              <th onClick={() => requestSort('TasksID')}>
                מספר {sortConfig.key === 'TasksID' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th onClick={() => requestSort('Description')}>
                תיאור {sortConfig.key === 'Description' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th onClick={() => requestSort('Status')}>
                סטטוס {sortConfig.key === 'Status' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th onClick={() => requestSort('Priority')}>
                עדיפות {sortConfig.key === 'Priority' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th onClick={() => requestSort('Date')}>
                תאריך יעד {sortConfig.key === 'Date' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th onClick={() => requestSort('TaskUser')}>
                אחראי {sortConfig.key === 'TaskUser' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((task, index) => (
                <tr key={task.TasksID || task.tasksID || `task-${index}`}>
                  <td className={styles.taskNumber}>#{task.TasksID || task.tasksID}</td>
                  <td className={styles.taskDescription}>
                    {task.Description || task.description}
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${getStatusClass(task.Status || task.status)}`}>
                      {task.Status || task.status}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.priorityBadge} ${getPriorityClass(task.Priority || task.priority)}`}>
                      {task.Priority || task.priority}
                    </span>
                  </td>
                  <td>{formatDate(task.Date || task.date)}</td>
                  <td className={styles.taskUser}>
                    {task.TaskUser || task.taskUser}
                  </td>
                  <td className={styles.actionsCell}>
                    <ActionMenu
                      task={task}
                      onEdit={startEditTask}
                      onDelete={handleDeleteTask}
                      onWhatsAppShare={handleWhatsAppShare}
                      onGenerateLink={handleGenerateExternalLink}
                      onEmailShare={handleEmailShare}
                      loading={loading}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className={styles.emptyState}>
                  {searchTerm || statusFilter || priorityFilter ? 'לא נמצאו משימות התואמות לחיפוש' : 'אין משימות'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Render modal content
  const renderModal = () => {
    if (!showNewTaskForm && !editingTask) return null;

    return (
      <div className={styles.modalOverlay} onClick={handleBackdropClick}>
        <div className={styles.modalContent}>
          {/* Modal Header */}
          <div className={styles.modalHeader}>
            <h3>{editingTask ? "עריכת משימה" : "הוספת משימה חדשה"}</h3>
            <button className={styles.modalCloseButton} onClick={closeModal} type="button">
              ✕
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {/* Modal Body */}
          <div className={styles.modalBody}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>תיאור:</label>
                <input
                  type="text"
                  name="Description"
                  value={editingTask ? editingTask.Description || "" : newTask.Description}
                  onChange={handleTaskInputChange}
                  placeholder="הכנס תיאור משימה"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>סטטוס:</label>
                <select
                  name="Status"
                  value={editingTask ? editingTask.Status || "" : newTask.Status}
                  onChange={handleTaskInputChange}
                >
                  <option value="פתוח">פתוח</option>
                  <option value="בטיפול">בטיפול</option>
                  <option value="הושלם">הושלם</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>עדיפות:</label>
                <select
                  name="Priority"
                  value={editingTask ? editingTask.Priority || "" : newTask.Priority}
                  onChange={handleTaskInputChange}
                >
                  <option value="נמוך">נמוך</option>
                  <option value="בינוני">בינוני</option>
                  <option value="גבוה">גבוה</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>תאריך יעד:</label>
                <input
                  type="date"
                  name="Date"
                  value={editingTask ? editingTask.Date || "" : newTask.Date}
                  onChange={handleTaskInputChange}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>אחראי:</label>
                <input
                  type="text"
                  name="TaskUser"
                  value={editingTask ? editingTask.TaskUser || "" : newTask.TaskUser}
                  onChange={handleTaskInputChange}
                  placeholder="שם האחראי"
                  required
                />
              </div>

              {/* Image Upload Section */}
              <div className={styles.formGroup}>
                <label>העלאת תמונה/קובץ:</label>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                />

                <input
                  type="file"
                  ref={cameraInputRef}
                  onChange={handleCameraCapture}
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                />

                <div className={styles.uploadButtons}>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={styles.uploadButton}
                    disabled={isUploading}
                  >
                    <Upload size={16} />
                    בחר קובץ
                  </button>

                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className={styles.cameraButton}
                    disabled={isUploading}
                  >
                    <Camera size={16} />
                    צלם תמונה
                  </button>
                </div>

                {isUploading && (
                  <div className={styles.uploadStatus}>
                    <span className={styles.spinner}>⏳</span>
                    מעלה קובץ לשרת...
                  </div>
                )}

                {((editingTask && editingTask.uploadedFile) || (newTask && newTask.uploadedFile)) && (
                  <div className={styles.uploadedFileInfo}>
                    <div className={styles.fileDetails}>
                      <span className={styles.fileName}>
                        📎 {editingTask ? editingTask.uploadedFile?.name : newTask.uploadedFile?.name}
                      </span>
                      <span className={styles.fileSize}>
                        ({formatFileSize(editingTask ? editingTask.uploadedFile?.size : newTask.uploadedFile?.size)})
                      </span>
                      <span className={styles.filePath}>
                        שרת: {editingTask ? editingTask.uploadedFile?.path : newTask.uploadedFile?.path}
                      </span>
                    </div>
                    <div className={styles.fileActions}>
                      <button
                        type="button"
                        onClick={viewTaskImage}
                        className={styles.viewFileButton}
                        title="צפה בקובץ"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={removeUploadedFile}
                        className={styles.removeFileButton}
                        title="הסר קובץ"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Show existing task image if in edit mode and has TaskImageURL but no uploaded file */}
                {editingTask && editingTask.TaskImageURL && !editingTask.uploadedFile && (
                  <div className={styles.existingImageInfo}>
                    <div className={styles.fileDetails}>
                      <span className={styles.fileName}>📁 תמונה קיימת</span>
                      <span className={styles.filePath}>{editingTask.TaskImageURL}</span>
                    </div>
                    <div className={styles.fileActions}>
                      <button
                        type="button"
                        onClick={viewTaskImage}
                        className={styles.viewFileButton}
                        title="צפה בתמונה"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={removeExistingImage}
                        className={styles.removeFileButton}
                        title="מחק תמונה קיימת"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Fallback for manual URL input */}
                <div className={styles.manualUrlSection}>
                  <label>או הכנס קישור תמונה ידנית:</label>
                  <input
                    type="url"
                    name="TaskImageURL"
                    value={editingTask ? editingTask.TaskImageURL || "" : newTask.TaskImageURL}
                    onChange={handleTaskInputChange}
                    placeholder="קישור לתמונה (אופציונלי)"
                    disabled={isUploading}
                  />
                </div>
              </div>
            </div>

            {/* Suggested Vendors - Only show in create mode, not in edit mode */}
            {!editingTask && (
              <div className={styles.suggestedVendors}>
                <div className={styles.vendorsHeader}>
                  <h4>ספקים מומלצים ({suggestedVendors.length})</h4>
                  {suggestedVendors.length > 0 && (
                    <button
                      type="button"
                      className={styles.collapseButton}
                      onClick={() => setIsVendorsCollapsed(!isVendorsCollapsed)}
                    >
                      {isVendorsCollapsed ? '🔽 הצג ספקים' : '🔼 הסתר ספקים'}
                    </button>
                  )}
                </div>

              {suggestedVendors.length > 0 && !isVendorsCollapsed ? (
                <>
                  {suggestedVendors.length > 3 && (
                    <div className={styles.vendorsInfo}>
                      <span className={styles.vendorsCount}>
                        מציג {showAllVendors ? suggestedVendors.length : Math.min(3, suggestedVendors.length)} מתוך {suggestedVendors.length} ספקים
                      </span>
                      {!showAllVendors && suggestedVendors.length > 3 && (
                        <button
                          type="button"
                          className={styles.showMoreButton}
                          onClick={() => setShowAllVendors(true)}
                        >
                          הצג עוד {suggestedVendors.length - 3} ספקים
                        </button>
                      )}
                      {showAllVendors && suggestedVendors.length > 3 && (
                        <button
                          type="button"
                          className={styles.showLessButton}
                          onClick={() => setShowAllVendors(false)}
                        >
                          הצג פחות ספקים
                        </button>
                      )}
                    </div>
                  )}
                  <div className={styles.vendorsGrid}>
                    {(showAllVendors ? suggestedVendors : suggestedVendors.slice(0, 3)).map((vendor, index) => (
                      <div
                        key={vendor.supplierID || vendor.id || index}
                        className={`${styles.vendorCard} ${vendor.isHighPriority ? styles.highPriorityVendor : ''}`}
                      >
                        <div className={styles.vendorHeader}>
                          <div className={styles.vendorName}>{vendor.name}</div>
                          <div className={styles.priorityIndicator}>
                            {vendor.isHighPriority ? (
                              <span className={styles.highPriority}>התאמה גבוהה</span>
                            ) : (
                              <span className={styles.normalPriority}>התאמה רגילה</span>
                            )}
                            <span className={styles.scoreIndicator}>{vendor.relevanceScore} נק'</span>
                            <span className={`${styles.supplierPriorityBadge} ${vendor.supplierPriority <= 3 ? styles.highSupplierPriority : vendor.supplierPriority <= 6 ? styles.mediumSupplierPriority : styles.lowSupplierPriority}`}>
                              עדיפות: {vendor.supplierPriority || 10}
                            </span>
                          </div>
                        </div>

                        <div className={styles.vendorServices}>
                          <strong>שירותים תואמים:</strong>
                          <div className={styles.matchedServices}>
                            {vendor.matchedServices && vendor.matchedServices.length > 0 ? (
                              vendor.matchedServices.map((service, idx) => (
                                <span key={idx} className={styles.serviceTag}>
                                  {service}
                                </span>
                              ))
                            ) : (
                              <span>כל השירותים: {vendor.serviceType}</span>
                            )}
                          </div>
                        </div>

                        <div className={styles.vendorContact}>
                          {vendor.contactPerson && <div><strong>איש קשר:</strong> {vendor.contactPerson}</div>}
                          {vendor.phone && (
                            <div>
                              <strong>טלפון:</strong>{' '}
                              <a
                                href={`tel:${vendor.phone.replace(/\s|-/g, '')}`}
                                className={styles.phoneLink}
                                title={`התקשר ל-${vendor.phone}`}
                              >
                                {vendor.phone}
                              </a>
                            </div>
                          )}
                          {vendor.email && (
                            <div>
                              <strong>אימייל:</strong>{' '}
                              <a
                                href={`mailto:${vendor.email}`}
                                className={styles.emailLink}
                                title={`שלח אימייל ל-${vendor.email}`}
                              >
                                {vendor.email}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : suggestedVendors.length === 0 ? (
                <div className={styles.noVendorsMessage}>
                  <div className={styles.noVendorsIcon}>🔍</div>
                  <div className={styles.noVendorsText}>
                    <p><strong>לא נמצאו ספקים מתאימים</strong></p>
                    <p>הכנס תיאור מפורט יותר כדי לקבל המלצות על ספקים רלוונטיים</p>
                  </div>
                </div>
              ) : null}

                {/* Priority Legend - Only show when vendors exist */}
                <div className={styles.priorityLegend}>
                  {suggestedVendors.length > 0 ? (
                    <>
                      <div className={styles.legendItem}>
                        <span className={styles.highPriorityDot}></span>
                        <span>התאמה גבוהה - ספקים עם התאמה מדויקת לתיאור המשימה</span>
                      </div>
                      <div className={styles.legendItem}>
                        <span className={styles.normalPriorityDot}></span>
                        <span>התאמה רגילה - ספקים עם התאמה חלקית לתיאור המשימה</span>
                      </div>
                    </>
                  ) : (
                    <div className={styles.legendItemEmpty}>
                      <span className={styles.legendEmptyIcon}>💡</span>
                      <span>המערכת תציג כאן הסבר על רמות ההתאמה כשיימצאו ספקים מתאימים</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className={styles.modalFooter}>
            <button
              className={styles.saveButton}
              onClick={editingTask ? handleUpdateTask : handleAddTask}
              disabled={loading}
              type="button"
            >
              {loading ? "שומר..." : (editingTask ? "עדכן משימה" : "הוסף משימה")}
            </button>

            <button
              className={styles.cancelButton}
              onClick={closeModal}
              disabled={loading}
              type="button"
            >
              ביטול
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.taskManagementContainer}>
      {/* Header with Statistics */}
      <div className={styles.header}>
        <h1>ניהול משימות ועד בית</h1>
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <div>
              <h3>סה"כ משימות</h3>
              <p className={styles.statValue}>{totalTasks}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <div>
              <h3>משימות פתוחות</h3>
              <p className={styles.statValue}>{openTasks}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <div>
              <h3>בטיפול</h3>
              <p className={styles.statValue}>{inProgressTasks}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <div>
              <h3>הושלמו</h3>
              <p className={styles.statValue}>{completedTasks}</p>
            </div>
          </div>
        </div>
      </div>

      {error && !showNewTaskForm && !editingTask && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* Actions and Filters */}
      <div className={styles.actions}>
        <div className={styles.filtersContainer}>
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="חיפוש משימות..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className={styles.filterGroup}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">כל הסטטוסים</option>
              <option value="פתוח">פתוח</option>
              <option value="בטיפול">בטיפול</option>
              <option value="הושלם">הושלם</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">כל העדיפויות</option>
              <option value="גבוה">גבוה</option>
              <option value="בינוני">בינוני</option>
              <option value="נמוך">נמוך</option>
            </select>
          </div>

          {(searchTerm || statusFilter || priorityFilter) && (
            <button
              className={styles.clearFiltersButton}
              onClick={clearFilters}
              title="נקה מסננים"
            >
              נקה
            </button>
          )}
        </div>

        <button
          className={styles.addButton}
          onClick={() => {
            setShowNewTaskForm(true);
            setSuggestedVendors([]);
          }}
          disabled={loading}
        >
          משימה חדשה +
        </button>
      </div>

      {/* Mobile Sort Options */}
      {isMobile && (
        <div className={styles.mobileSortContainer}>
          <label>מיון לפי:</label>
          <select
            onChange={(e) => requestSort(e.target.value)}
            className={styles.sortSelect}
          >
            <option value="">בחר מיון</option>
            <option value="TasksID">מספר משימה</option>
            <option value="Description">תיאור</option>
            <option value="Status">סטטוס</option>
            <option value="Priority">עדיפות</option>
            <option value="Date">תאריך יעד</option>
            <option value="TaskUser">אחראי</option>
          </select>
        </div>
      )}

      {/* Tasks Display */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>טוען משימות...</p>
        </div>
      ) : (
        <>
          {isMobile ? renderMobileCards() : renderDesktopTable()}
        </>
      )}

      {/* Pagination */}
      {filteredTasks.length > 0 && (
        <div className={styles.pagination}>
          <div className={styles.paginationControls}>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className={styles.paginationButton}
            >
              הקודם
            </button>
            <span className={styles.pageInfo}>
              עמוד {currentPage} מתוך {totalPages}
            </span>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              className={styles.paginationButton}
            >
              הבא
            </button>
          </div>

          <div className={styles.resultsPerPage}>
            <span>משימות בעמוד:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              disabled={loading}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>

          <div className={styles.totalInfo}>
            מציג {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredTasks.length)} מתוך {filteredTasks.length} משימות
          </div>
        </div>
      )}

      {/* Modal */}
      {renderModal()}

      {/* Task Image Modal */}
      {selectedTaskImage && (
        <div className={styles.imageModalOverlay} onClick={() => setSelectedTaskImage(null)}>
          <div className={styles.imageModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.imageModalHeader}>
              <h3>תמונת משימה</h3>
              <button
                className={styles.imageModalClose}
                onClick={() => setSelectedTaskImage(null)}
              >
                ✕
              </button>
            </div>
            <div className={styles.imageContainer}>
              <img
                src={selectedTaskImage}
                alt="תמונת משימה"
                className={styles.modalImage}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}